import type {PayloadAction} from '@reduxjs/toolkit'
import {createSlice} from '@reduxjs/toolkit'

// --- V2 Types (completely separate from V1) ---
export type BuildPipelineV2 = {
  organization?: string
  projectName?: string
  buildJob: string | {id: string; label: string}
  buildDurationThreshold: string
  id: string
}

export type DeployV2 = {
  organization?: string
  projectName?: string
  deployJob: string | {id: string; label: string}
  deployProdBranch: string
  deployHotfixBranch: string
  deployProdEnvSuffix: string
  id: string
}

// --- Build V2 ---
interface BuildStateV2 {
  buildDurationThreshold: string
  pipelines: BuildPipelineV2[]
}

export const initialBuildStateV2: BuildStateV2 = {
  buildDurationThreshold: '',
  pipelines: [],
}

const buildSliceV2 = createSlice({
  name: 'buildV2',
  initialState: initialBuildStateV2,
  reducers: {
    setBuildPipelinesV2: (state, action: PayloadAction<BuildPipelineV2[]>) => {
      state.pipelines = action.payload
    },
    setBuildDurationThresholdV2: (state, action: PayloadAction<string>) => {
      state.buildDurationThreshold = action.payload
    },
    setBuildV2: (state, action: PayloadAction<BuildStateV2>) => {
      state.pipelines = action.payload.pipelines
      state.buildDurationThreshold = action.payload.buildDurationThreshold
    },
  },
})

export const {setBuildPipelinesV2, setBuildDurationThresholdV2, setBuildV2} = buildSliceV2.actions
export const buildReducerV2 = buildSliceV2.reducer

// --- Deploy V2 ---
const initialDeployV2: DeployV2 = {
  organization: '',
  projectName: '',
  deployJob: '',
  deployProdBranch: '',
  deployHotfixBranch: '',
  deployProdEnvSuffix: '',
  id: '',
}

const deploySliceV2 = createSlice({
  name: 'deployV2',
  initialState: [initialDeployV2] as DeployV2[],
  reducers: {
    setDeployPipelinesV2: (state, action: PayloadAction<DeployV2[]>) => action.payload,
  },
})

export const {setDeployPipelinesV2} = deploySliceV2.actions
export const deployReducerV2 = deploySliceV2.reducer

// --- Repo V2 ---
export type RepoV2 = {
  organization?: string
  projectName?: string
  repoUrl: string | {id: string; label: string}
  branches: string[]
  id: string
  collectorItemId?: string
  modified?: boolean // Track if user has modified this repo
}

const initialRepoV2: RepoV2 = {
  organization: '',
  projectName: '',
  repoUrl: '',
  branches: [''],
  id: '',
}

const repoSliceV2 = createSlice({
  name: 'repoV2',
  initialState: [initialRepoV2] as RepoV2[],
  reducers: {
    setReposV2: (state, action: PayloadAction<RepoV2[]>) => action.payload,
  },
})

export const {setReposV2} = repoSliceV2.actions
export const repoReducerV2 = repoSliceV2.reducer

// --- SecurityAnalysis V2 ---
export type SecurityAnalysisV2 = {
  organization?: string
  projectName?: string
  url: string | {id: string; label: string}
  id: string
  collectorItemId?: string
  modified?: boolean
}

const initialSecurityAnalysisV2: SecurityAnalysisV2 = {
  organization: '',
  projectName: '',
  url: '',
  id: '',
}

const securityAnalysisSliceV2 = createSlice({
  name: 'securityAnalysisV2',
  initialState: [initialSecurityAnalysisV2] as SecurityAnalysisV2[],
  reducers: {
    setSecurityAnalysisV2: (state, action: PayloadAction<SecurityAnalysisV2[]>) => action.payload,
  },
})

export const {setSecurityAnalysisV2} = securityAnalysisSliceV2.actions
export const securityAnalysisReducerV2 = securityAnalysisSliceV2.reducer
